/**
 * Income System Page
 * 
 * A calm command center for household income.
 * Designed to feel emotionally safe for users stressed by money.
 * 
 * Design principles:
 * - Generous spacing
 * - Soft card panels
 * - Minimal borders
 * - Strong alignment
 * - Quiet status pills
 * - Simple action menus
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  Plus,
  MoreHorizontal,
  Loader2,
  Info,
  CheckCircle2,
  XCircle,
  Edit,
  Zap,
  Trash2,
  RefreshCw
} from "lucide-react";

import { FetchErrorState } from "../components/FetchErrorState";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============== Response Parser ==============
// Parse response body EXACTLY ONCE to avoid "body stream already read" errors
async function parseResponse(res) {
  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  let payload = null;

  try {
    payload = isJson ? await res.json() : await res.text();
  } catch (e) {
    // Body might be empty or malformed
    payload = null;
  }

  if (!res.ok) {
    const message =
      (payload && (payload.detail || payload.message || payload.error)) ||
      (typeof payload === "string" && payload) ||
      `Request failed (${res.status})`;
    throw new Error(message);
  }

  return payload;
}

// ============== Constants ==============
const INCOME_TYPES = [
  { value: 'salary', label: 'Salary' },
  { value: 'wages', label: 'Wages' },
  { value: 'commission', label: 'Commission' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'dividends', label: 'Dividends' },
  { value: 'interest', label: 'Interest' },
  { value: 'rental', label: 'Rental Income' },
  { value: 'business', label: 'Business Income' },
  { value: 'freelance', label: 'Freelance' },
  { value: 'benefits', label: 'Benefits' },
  { value: 'pension', label: 'Pension' },
  { value: 'child_support', label: 'Child Support' },
  { value: 'government', label: 'Government Payment' },
  { value: 'side_hustle', label: 'Side Hustle' },
  { value: 'gift', label: 'Gift' },
  { value: 'other', label: 'Other' },
];

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'twice_monthly', label: 'Twice Monthly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
  { value: 'irregular', label: 'Irregular' },
];

const VARIABILITY_OPTIONS = [
  { value: 'fixed', label: 'Fixed', description: 'Same each time' },
  { value: 'variable', label: 'Variable', description: 'Fluctuates' },
  { value: 'range', label: 'Range', description: 'Min to max' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

// ============== Helpers ==============
function formatCurrency(amount, currency = 'NZD') {
  return new Intl.NumberFormat('en-NZ', { 
    style: 'currency', 
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0 
  }).format(amount || 0);
}

function formatDateShort(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' });
}

function getDaysUntil(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return Math.ceil((date - today) / (1000 * 60 * 60 * 24));
}

function getFrequencyLabel(frequency) {
  return FREQUENCIES.find(f => f.value === frequency)?.label || frequency;
}

function formatAmountDisplay(source, currency) {
  if (source.variability === 'range' && source.amount_min != null && source.amount_max != null) {
    return `${formatCurrency(source.amount_min, currency)} – ${formatCurrency(source.amount_max, currency)}`;
  }
  if (source.variability === 'variable') {
    return `~${formatCurrency(source.expected_amount, currency)}`;
  }
  return formatCurrency(source.expected_amount, currency);
}

// ============== Status Pill ==============
function StatusPill({ status }) {
  const config = {
    expected: { label: 'Expected', bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400' },
    pending: { label: 'Late', bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
    received: { label: 'Received', bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400' },
    partial: { label: 'Partial', bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
    missed: { label: 'Missed', bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400' },
  };
  const c = config[status] || config.expected;
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

// ============== Overview Panel ==============
function OverviewPanel({ dashboard, currency, loading }) {
  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-sm animate-pulse">
        <div className="h-6 bg-slate-100 dark:bg-slate-800 rounded w-32 mb-8" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map(i => (
            <div key={i}>
              <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded w-20 mb-3" />
              <div className="h-8 bg-slate-100 dark:bg-slate-800 rounded w-28" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const week = dashboard?.this_week || {};
  const expected = (week.expected_total || 0) + (week.pending_total || 0);
  const received = week.received_total || 0;
  const diff = received - expected;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-sm" data-testid="income-overview-panel">
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
          Income
        </h1>
        <p className="text-slate-500 dark:text-slate-400">
          Know what should be coming in, what arrived, and what needs attention.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">
            Expected This Week
          </p>
          <p className="font-heading text-2xl font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
            {formatCurrency(expected, currency)}
          </p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">
            Received This Week
          </p>
          <p className="font-heading text-2xl font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
            {formatCurrency(received, currency)}
          </p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">
            Difference
          </p>
          {diff > 0 ? (
            <p className="font-heading text-2xl font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">
              +{formatCurrency(diff, currency)}
            </p>
          ) : diff < 0 ? (
            <p className="font-heading text-2xl font-semibold text-amber-600 dark:text-amber-400 tabular-nums">
              {formatCurrency(Math.abs(diff), currency)} <span className="text-base font-normal">pending</span>
            </p>
          ) : (
            <p className="font-heading text-2xl font-semibold text-slate-400 tabular-nums">—</p>
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">
            Next Income
          </p>
          {dashboard?.next_income_date ? (
            <p className="font-heading text-xl font-semibold text-slate-900 dark:text-slate-100">
              {formatDateShort(dashboard.next_income_date)}
            </p>
          ) : (
            <p className="text-slate-400">—</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============== Upcoming Income ==============
function UpcomingIncomePanel({ events, currency, onMarkReceived, onMarkMissed, onEditEvent, onChangeStatus, onAddEvent, loading }) {
  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-sm animate-pulse">
        <div className="h-5 bg-slate-100 dark:bg-slate-800 rounded w-40 mb-6" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-50 dark:bg-slate-800/50 rounded-xl" />)}
        </div>
      </div>
    );
  }

  const sorted = [...events]
    .filter(e => e.status !== 'received')
    .sort((a, b) => new Date(a.expected_date) - new Date(b.expected_date))
    .slice(0, 12);

  const receivedEvents = [...events]
    .filter(e => e.status === 'received')
    .sort((a, b) => new Date(b.expected_date) - new Date(a.expected_date))
    .slice(0, 6);

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-sm" data-testid="upcoming-income-panel">
      <div className="mb-6">
        <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
          Upcoming Income
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          What's expected, what's arrived, and what may need attention.
        </p>
      </div>

      {sorted.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-400 dark:text-slate-500 mb-4">Nothing set up yet for this cycle</p>
          <Button onClick={onAddEvent} variant="outline" size="sm" className="rounded-full">
            <Plus className="w-4 h-4 mr-2" />
            Add income
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map((event) => {
            const days = getDaysUntil(event.expected_date);
            const isLate = days !== null && days < 0 && event.status === 'expected';
            const status = isLate ? 'pending' : event.status;

            return (
              <div
                key={event.event_id}
                className="group flex items-center justify-between p-4 rounded-xl bg-slate-50/50 dark:bg-slate-800/30 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                data-testid={`income-event-${event.event_id}`}
              >
                <div className="flex-1 min-w-0 mr-4">
                  <p className="font-medium text-slate-900 dark:text-slate-100 truncate">
                    {event.source_name}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {formatDateShort(event.expected_date)}
                    {event.source_recurring && (
                      <span className="inline-flex items-center gap-0.5 ml-1.5 text-xs text-[#1a4d3e] dark:text-emerald-400">
                        <RefreshCw className="w-3 h-3" />
                        {event.source_frequency === 'weekly' ? 'Weekly' : event.source_frequency === 'fortnightly' ? 'Fortnightly' : event.source_frequency === 'monthly' ? 'Monthly' : ''}
                      </span>
                    )}
                    {days !== null && days >= 0 && days <= 7 && (
                      <span className="ml-2 text-slate-400">
                        {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days} days`}
                      </span>
                    )}
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-medium tabular-nums text-slate-900 dark:text-slate-100">
                      {formatCurrency(event.expected_amount, currency)}
                    </p>
                    {event.actual_amount && event.actual_amount !== event.expected_amount && (
                      <p className="text-sm tabular-nums text-slate-500">
                        {formatCurrency(event.actual_amount, currency)} actual
                      </p>
                    )}
                  </div>

                  <div className="w-20 text-right">
                    <StatusPill status={status} />
                  </div>

                  <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`event-menu-${event.event_id}`}
                        >
                          <MoreHorizontal className="w-4 h-4 text-slate-400" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => onEditEvent(event)} data-testid={`edit-event-${event.event_id}`}>
                          Edit event
                        </DropdownMenuItem>
                        {status !== 'received' && (
                          <DropdownMenuItem onClick={() => onMarkReceived(event)} data-testid={`mark-received-${event.event_id}`}>
                            Mark as received
                          </DropdownMenuItem>
                        )}
                        {status === 'received' && (
                          <DropdownMenuItem onClick={() => onChangeStatus(event, 'expected')} data-testid={`revert-expected-${event.event_id}`}>
                            Revert to expected
                          </DropdownMenuItem>
                        )}
                        {status !== 'missed' && status !== 'received' && (
                          <DropdownMenuItem onClick={() => onMarkMissed(event)} className="text-amber-600" data-testid={`mark-missed-${event.event_id}`}>
                            Mark as missed
                          </DropdownMenuItem>
                        )}
                        {status === 'missed' && (
                          <DropdownMenuItem onClick={() => onChangeStatus(event, 'expected')}>
                            Revert to expected
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                </div>
              </div>
            );
          })}

          <div className="pt-4">
            <Button onClick={onAddEvent} variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
              <Plus className="w-4 h-4 mr-2" />
              Add income event
            </Button>
          </div>
        </div>
      )}

      {/* Recently Received */}
      {receivedEvents.length > 0 && (
        <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800" data-testid="received-income-section">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Recently Received</h3>
          <div className="space-y-2">
            {receivedEvents.map((event) => (
              <div
                key={event.event_id}
                className="group flex items-center justify-between p-3 rounded-lg bg-emerald-50/30 dark:bg-emerald-900/10"
                data-testid={`received-event-${event.event_id}`}
              >
                <div className="flex-1 min-w-0 mr-4">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">{event.source_name}</p>
                  <p className="text-xs text-slate-400">{formatDateShort(event.actual_date || event.expected_date)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-sm font-medium tabular-nums text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(event.actual_amount || event.expected_amount, currency)}
                  </p>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`received-menu-${event.event_id}`}>
                        <MoreHorizontal className="w-3.5 h-3.5 text-slate-400" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => onEditEvent(event)}>Edit event</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onChangeStatus(event, 'expected')}>Revert to expected</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============== Income Sources ==============
function IncomeSourcesPanel({ sources, accounts, currency, onAdd, onEdit, onDelete, onToggleActive, loading }) {
  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-sm animate-pulse">
        <div className="h-5 bg-slate-100 dark:bg-slate-800 rounded w-40 mb-6" />
        <div className="space-y-4">
          {[1, 2].map(i => <div key={i} className="h-20 bg-slate-50 dark:bg-slate-800/50 rounded-xl" />)}
        </div>
      </div>
    );
  }

  const getAccountName = (id) => {
    const acc = accounts.find(a => a.account_id === id);
    return acc ? (acc.display_name || acc.name) : null;
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-sm" data-testid="income-sources-panel">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
            Income Sources
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Where your money comes from this cycle.
          </p>
        </div>
        <Button onClick={onAdd} size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900">
          <Plus className="w-4 h-4 mr-2" />
          Add source
        </Button>
      </div>

      {sources.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
          <p className="text-slate-400 dark:text-slate-500 mb-1">Add your income for this cycle to get started</p>
          <p className="text-xs text-muted-foreground/70 mb-4">Add your salary, benefits, or side income so your plan has something to work with.</p>
          <Button onClick={onAdd} variant="outline" size="sm" className="rounded-full">
            <Plus className="w-4 h-4 mr-2" />
            Add income
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {sources.map((source) => {
            const account = getAccountName(source.account_id);

            return (
              <div
                key={source.source_id}
                className={`group flex items-center justify-between p-4 rounded-xl transition-colors ${
                  source.is_active 
                    ? 'bg-slate-50/50 dark:bg-slate-800/30 hover:bg-slate-50 dark:hover:bg-slate-800/50' 
                    : 'bg-slate-50/30 dark:bg-slate-800/20 opacity-60'
                }`}
                data-testid={`income-source-${source.source_id}`}
              >
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900 dark:text-slate-100 truncate">
                      {source.name}
                    </p>
                    {!source.is_active && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {getFrequencyLabel(source.frequency)}
                    {source.recurring && (
                      <span className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded-full text-xs bg-[#1a4d3e]/10 text-[#1a4d3e] dark:bg-[#1a4d3e]/20 dark:text-emerald-400">
                        <RefreshCw className="w-3 h-3" />
                        Repeats
                      </span>
                    )}
                    {source.owner_name && <span> · {source.owner_name}</span>}
                    {account && <span> · {account}</span>}
                    {source.matching_rules?.description_keywords?.length > 0 && (
                      <span className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded-full text-xs bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400">
                        <Zap className="w-3 h-3" />
                        Auto-match
                      </span>
                    )}
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <p className="font-medium tabular-nums text-slate-900 dark:text-slate-100">
                    {formatAmountDisplay(source, currency)}
                  </p>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreHorizontal className="w-4 h-4 text-slate-400" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem onClick={() => onEdit(source)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onToggleActive(source)}>
                        {source.is_active ? (
                          <>
                            <XCircle className="w-4 h-4 mr-2" />
                            Mark inactive
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Mark active
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => onDelete(source)} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============== Info Note ==============
function InfoNote() {
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
      <Info className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Income can be tracked manually now and matched automatically later when bank syncing is active.
      </p>
    </div>
  );
}

// ============== Dialogs ==============
function SourceDialog({ open, onOpenChange, source, onSave, members, accounts, loading }) {
  const isEdit = !!source?.source_id;
  const [form, setForm] = useState({
    name: '', income_type: 'salary', expected_amount: '', frequency: 'monthly',
    variability: 'fixed', amount_min: '', amount_max: '',
    next_expected_date: new Date().toISOString().split('T')[0],
    owner_user_id: '', account_id: '', notes: '',
    matching_keywords: '', matching_tolerance: '10', matching_window: '5',
    recurring: false, auto_generate: true,
    anchor_day_of_week: '', anchor_day_of_month: '',
  });

  useEffect(() => {
    if (source) {
      const rules = source.matching_rules || {};
      setForm({
        name: source.name || '',
        income_type: source.income_type || 'salary',
        expected_amount: source.expected_amount || '',
        frequency: source.frequency || 'monthly',
        variability: source.variability || 'fixed',
        amount_min: source.amount_min || '',
        amount_max: source.amount_max || '',
        next_expected_date: source.next_expected_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        owner_user_id: source.owner_user_id || '',
        account_id: source.account_id || '',
        notes: source.notes || '',
        matching_keywords: (rules.description_keywords || []).join(', '),
        matching_tolerance: rules.amount_tolerance_percent ?? '10',
        matching_window: rules.date_window_days ?? '5',
        recurring: source.recurring ?? false,
        auto_generate: source.auto_generate ?? true,
        anchor_day_of_week: source.anchor_day_of_week ?? '',
        anchor_day_of_month: source.anchor_day_of_month ?? '',
      });
    } else {
      setForm({
        name: '', income_type: 'salary', expected_amount: '', frequency: 'monthly',
        variability: 'fixed', amount_min: '', amount_max: '',
        next_expected_date: new Date().toISOString().split('T')[0],
        owner_user_id: '', account_id: '', notes: '',
        matching_keywords: '', matching_tolerance: '10', matching_window: '5',
        recurring: false, auto_generate: true,
        anchor_day_of_week: '', anchor_day_of_month: '',
      });
    }
  }, [source, open]);

  // When recurring frequency or anchor day changes, update next_expected_date to match
  const updateStartDate = (freq, dayOfWeek, dayOfMonth) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if ((freq === 'weekly' || freq === 'fortnightly') && dayOfWeek !== '' && dayOfWeek !== null) {
      const dow = parseInt(dayOfWeek);
      // JS: 0=Sun,1=Mon...6=Sat → our model: 0=Mon...6=Sun
      const jsDow = dow === 6 ? 0 : dow + 1;
      const todayDow = today.getDay();
      let diff = jsDow - todayDow;
      if (diff <= 0) diff += 7;
      const next = new Date(today);
      next.setDate(today.getDate() + diff);
      return next.toISOString().split('T')[0];
    }
    if (freq === 'monthly' && dayOfMonth !== '' && dayOfMonth !== null) {
      const dom = parseInt(dayOfMonth);
      const next = new Date(today.getFullYear(), today.getMonth(), dom);
      if (next <= today) next.setMonth(next.getMonth() + 1);
      return next.toISOString().split('T')[0];
    }
    return null;
  };

  const handleSubmit = () => {
    if (!form.name.trim()) { toast.error("Please enter a name"); return; }
    
    let amt = parseFloat(form.expected_amount);
    if (form.variability === 'range') {
      const min = parseFloat(form.amount_min), max = parseFloat(form.amount_max);
      if (!min || !max || min > max) { toast.error("Please enter valid min/max"); return; }
      amt = (min + max) / 2;
    } else if (!amt || amt <= 0) {
      toast.error("Please enter a valid amount"); return;
    }

    // Build matching rules if keywords are provided
    const keywords = form.matching_keywords
      .split(',')
      .map(k => k.trim())
      .filter(Boolean);
    
    const matching_rules = keywords.length > 0 ? {
      description_keywords: keywords,
      account_id: form.account_id || null,
      amount_tolerance_percent: parseFloat(form.matching_tolerance) || 10,
      date_window_days: parseInt(form.matching_window) || 5
    } : null;

    onSave({
      ...form, expected_amount: amt,
      amount_min: form.variability === 'range' ? parseFloat(form.amount_min) : null,
      amount_max: form.variability === 'range' ? parseFloat(form.amount_max) : null,
      matching_rules,
      recurring: form.recurring,
      auto_generate: form.recurring ? form.auto_generate : false,
      anchor_day_of_week: (form.recurring && (form.frequency === 'weekly' || form.frequency === 'fortnightly') && form.anchor_day_of_week !== '') ? parseInt(form.anchor_day_of_week) : null,
      anchor_day_of_month: (form.recurring && form.frequency === 'monthly' && form.anchor_day_of_month !== '') ? parseInt(form.anchor_day_of_month) : null,
      source_id: source?.source_id
    });
  };

  const showDayOfWeek = form.recurring && (form.frequency === 'weekly' || form.frequency === 'fortnightly');
  const showDayOfMonth = form.recurring && form.frequency === 'monthly';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" aria-describedby="source-desc">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? 'Edit income' : 'Add income'}</DialogTitle>
        </DialogHeader>
        <p id="source-desc" className="sr-only">Income source form</p>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Name</Label>
            <Input
              placeholder="e.g., Main Salary"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-lg"
              data-testid="source-name-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Type</Label>
              <Select value={form.income_type} onValueChange={(v) => setForm({ ...form, income_type: v })}>
                <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INCOME_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Amount type</Label>
              <div className="flex gap-1.5">
                {VARIABILITY_OPTIONS.map(o => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setForm({ ...form, variability: o.value })}
                    className={`flex-1 py-2 px-2 rounded-lg text-xs transition-colors ${
                      form.variability === o.value
                        ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {form.variability === 'range' ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Min amount</Label>
                <Input type="number" min="0" value={form.amount_min} onChange={(e) => setForm({ ...form, amount_min: e.target.value })} className="rounded-lg" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Max amount</Label>
                <Input type="number" min="0" value={form.amount_max} onChange={(e) => setForm({ ...form, amount_max: e.target.value })} className="rounded-lg" />
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">{form.variability === 'variable' ? 'Typical amount' : 'Amount'}</Label>
              <Input type="number" min="0" value={form.expected_amount} onChange={(e) => setForm({ ...form, expected_amount: e.target.value })} className="rounded-lg" data-testid="source-amount-input" />
            </div>
          )}

          {/* ── Income Rhythm ── */}
          <div className="border-t pt-4 mt-2">
            <div className="flex items-center gap-2 mb-3">
              <RefreshCw className="w-3.5 h-3.5 text-slate-400" />
              <Label className="text-xs font-medium text-slate-700 dark:text-slate-300">Income Rhythm</Label>
            </div>

            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => setForm({ ...form, recurring: false })}
                className={`flex-1 py-2.5 px-3 rounded-lg text-sm transition-colors ${
                  !form.recurring
                    ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400'
                }`}
                data-testid="rhythm-oneoff-btn"
              >
                One-off
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, recurring: true })}
                className={`flex-1 py-2.5 px-3 rounded-lg text-sm transition-colors ${
                  form.recurring
                    ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400'
                }`}
                data-testid="rhythm-repeats-btn"
              >
                Repeats
              </button>
            </div>

            {form.recurring && (
              <div className="space-y-3 animate-in fade-in-0 slide-in-from-top-2 duration-200">
                {/* Frequency */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500">Frequency</Label>
                  <div className="flex gap-1.5">
                    {[
                      { value: 'weekly', label: 'Weekly' },
                      { value: 'fortnightly', label: 'Fortnightly' },
                      { value: 'monthly', label: 'Monthly' },
                    ].map(f => (
                      <button
                        key={f.value}
                        type="button"
                        onClick={() => {
                          const newForm = { ...form, frequency: f.value, anchor_day_of_week: '', anchor_day_of_month: '' };
                          setForm(newForm);
                        }}
                        className={`flex-1 py-2 px-2 rounded-lg text-xs transition-colors ${
                          form.frequency === f.value
                            ? 'bg-[#1a4d3e] text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400'
                        }`}
                        data-testid={`freq-${f.value}-btn`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Day selector */}
                {showDayOfWeek && (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Which day?</Label>
                    <Select
                      value={form.anchor_day_of_week !== '' ? String(form.anchor_day_of_week) : ''}
                      onValueChange={(v) => {
                        const newDate = updateStartDate(form.frequency, v, form.anchor_day_of_month);
                        setForm({ ...form, anchor_day_of_week: v, ...(newDate ? { next_expected_date: newDate } : {}) });
                      }}
                    >
                      <SelectTrigger className="rounded-lg" data-testid="anchor-day-of-week">
                        <SelectValue placeholder="Select a day" />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_WEEK.map(d => (
                          <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {showDayOfMonth && (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Day of month</Label>
                    <Select
                      value={form.anchor_day_of_month !== '' ? String(form.anchor_day_of_month) : ''}
                      onValueChange={(v) => {
                        const newDate = updateStartDate(form.frequency, form.anchor_day_of_week, v);
                        setForm({ ...form, anchor_day_of_month: v, ...(newDate ? { next_expected_date: newDate } : {}) });
                      }}
                    >
                      <SelectTrigger className="rounded-lg" data-testid="anchor-day-of-month">
                        <SelectValue placeholder="Select date" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                          <SelectItem key={d} value={String(d)}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Start date */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500">Starting from</Label>
                  <Input
                    type="date"
                    value={form.next_expected_date}
                    onChange={(e) => setForm({ ...form, next_expected_date: e.target.value })}
                    className="rounded-lg"
                    data-testid="rhythm-start-date"
                  />
                </div>

                {/* Auto-generate toggle */}
                <label className="flex items-center justify-between py-2 cursor-pointer" data-testid="auto-generate-toggle">
                  <span className="text-sm text-slate-600 dark:text-slate-400">Automatically create future income events</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={form.auto_generate}
                    onClick={() => setForm({ ...form, auto_generate: !form.auto_generate })}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      form.auto_generate ? 'bg-[#1a4d3e]' : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                      form.auto_generate ? 'translate-x-[18px]' : 'translate-x-[3px]'
                    }`} />
                  </button>
                </label>
              </div>
            )}

            {!form.recurring && (
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Expected date</Label>
                <Input
                  type="date"
                  value={form.next_expected_date}
                  onChange={(e) => setForm({ ...form, next_expected_date: e.target.value })}
                  className="rounded-lg"
                  data-testid="oneoff-date-input"
                />
              </div>
            )}
          </div>

          {members?.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Owner</Label>
              <Select value={form.owner_user_id || 'household'} onValueChange={(v) => setForm({ ...form, owner_user_id: v === 'household' ? '' : v })}>
                <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="household">Household</SelectItem>
                  {members.map(m => <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {accounts?.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Paid into</Label>
              <Select value={form.account_id || 'none'} onValueChange={(v) => setForm({ ...form, account_id: v === 'none' ? '' : v })}>
                <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not specified</SelectItem>
                  {accounts.map(a => <SelectItem key={a.account_id} value={a.account_id}>{a.display_name || a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Auto-matching rules */}
          <div className="border-t pt-4 mt-2">
            <Label className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5" />
              Auto-match from bank feed
            </Label>
            <p className="text-xs text-muted-foreground mt-1 mb-3">
              Keywords to match incoming deposits. Comma-separated.
            </p>
            <div className="space-y-3">
              <Input
                placeholder="e.g., KETE TRUST, SALARY, PAYROLL"
                value={form.matching_keywords}
                onChange={(e) => setForm({ ...form, matching_keywords: e.target.value })}
                className="rounded-lg text-sm"
                data-testid="matching-keywords-input"
              />
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Amount tolerance %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={form.matching_tolerance}
                    onChange={(e) => setForm({ ...form, matching_tolerance: e.target.value })}
                    className="rounded-lg text-sm"
                    data-testid="matching-tolerance-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Date window (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="30"
                    value={form.matching_window}
                    onChange={(e) => setForm({ ...form, matching_window: e.target.value })}
                    className="rounded-lg text-sm"
                    data-testid="matching-window-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900" data-testid="save-source-btn">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? 'Save' : 'Add income'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EventDialog({ open, onOpenChange, sources, onSave, loading }) {
  const [form, setForm] = useState({ income_source_id: '', expected_amount: '', expected_date: new Date().toISOString().split('T')[0], notes: '' });

  useEffect(() => {
    if (open && sources.length) {
      setForm({ income_source_id: sources[0].source_id, expected_amount: sources[0].expected_amount || '', expected_date: new Date().toISOString().split('T')[0], notes: '' });
    }
  }, [open, sources]);

  const handleSourceChange = (id) => {
    const s = sources.find(x => x.source_id === id);
    setForm({ ...form, income_source_id: id, expected_amount: s?.expected_amount || '' });
  };

  const handleSubmit = () => {
    if (!form.income_source_id) { toast.error("Select a source"); return; }
    if (!form.expected_amount || parseFloat(form.expected_amount) <= 0) { toast.error("Enter valid amount"); return; }
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" aria-describedby="event-desc">
        <DialogHeader><DialogTitle className="font-heading">Add income event</DialogTitle></DialogHeader>
        <p id="event-desc" className="sr-only">One-off income event</p>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Source</Label>
            <Select value={form.income_source_id} onValueChange={handleSourceChange}>
              <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                {sources.map(s => <SelectItem key={s.source_id} value={s.source_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Amount</Label>
              <Input type="number" min="0" value={form.expected_amount} onChange={(e) => setForm({ ...form, expected_amount: e.target.value })} className="rounded-lg" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Date</Label>
              <Input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} className="rounded-lg" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Add event
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceivedDialog({ open, onOpenChange, event, onConfirm, currency, loading }) {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (event) {
      setAmount(event.expected_amount?.toString() || '');
      setDate(new Date().toISOString().split('T')[0]);
    }
  }, [event, open]);

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" aria-describedby="received-desc">
        <DialogHeader><DialogTitle className="font-heading">Mark as received</DialogTitle></DialogHeader>
        <p id="received-desc" className="sr-only">Confirm received amount</p>

        <div className="space-y-4 py-4">
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
            <p className="font-medium text-slate-900 dark:text-slate-100">{event.source_name}</p>
            <p className="text-sm text-slate-500">Expected {formatCurrency(event.expected_amount, currency)}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Amount received</Label>
              <Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-lg" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Date received</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-lg" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onConfirm({ event_id: event.event_id, actual_amount: parseFloat(amount), actual_date: date })} disabled={loading} className="rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Main ==============
export function IncomeContent() {
  const { household, accounts } = useAuth();
  const [sources, setSources] = useState([]);
  const [events, setEvents] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [saving, setSaving] = useState(false);

  const [sourceOpen, setSourceOpen] = useState(false);
  const [editSource, setEditSource] = useState(null);
  const [eventOpen, setEventOpen] = useState(false);
  const [receivedOpen, setReceivedOpen] = useState(false);
  const [markingEvent, setMarkingEvent] = useState(null);

  const currency = household?.currency || 'NZD';

  const fetchData = useCallback(async () => {
    if (!household?.household_id) return;
    setLoading(true);
    setFetchError(false);
    console.log("[Income] Fetching for household:", household.household_id);
    try {
      const [sRes, eRes, dRes, mRes] = await Promise.all([
        fetch(`${API_URL}/api/households/${household.household_id}/income/sources`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/income/events?limit=50`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/income/dashboard`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/members`, { credentials: 'include' })
      ]);
      if (!sRes.ok && !eRes.ok) {
        console.error(`[Income] Fetch failed: sources=${sRes.status}, events=${eRes.status}`);
        setFetchError(true);
        return;
      }
      if (sRes.ok) {
        const sData = await parseResponse(sRes);
        console.log("[Income] Loaded:", sData?.sources?.length || 0, "sources");
        setSources(sData?.sources || []);
      }
      if (eRes.ok) {
        const eData = await parseResponse(eRes);
        setEvents(eData?.events || []);
      }
      if (dRes.ok) {
        const dData = await parseResponse(dRes);
        setDashboard(dData);
      }
      if (mRes.ok) {
        const mData = await parseResponse(mRes);
        setMembers(mData || []);
      }
    } catch (e) {
      console.error("[Income] Network error:", e);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [household?.household_id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveSource = async (data) => {
    setSaving(true);
    try {
      const isEdit = !!data.source_id;
      const url = isEdit
        ? `${API_URL}/api/households/${household.household_id}/income/sources/${data.source_id}`
        : `${API_URL}/api/households/${household.household_id}/income/sources`;
      const res = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: data.name, income_type: data.income_type, expected_amount: data.expected_amount,
          frequency: data.frequency, variability: data.variability, amount_min: data.amount_min,
          amount_max: data.amount_max, next_expected_date: data.next_expected_date,
          owner_user_id: data.owner_user_id || null, account_id: data.account_id || null,
          matching_rules: data.matching_rules || null,
          recurring: data.recurring ?? false,
          auto_generate: data.auto_generate ?? false,
          anchor_day_of_week: data.anchor_day_of_week ?? null,
          anchor_day_of_month: data.anchor_day_of_month ?? null,
        })
      });
      // Parse response exactly once
      await parseResponse(res);
      toast.success(isEdit ? 'Updated' : 'Income added — your plan just got clearer');
      setSourceOpen(false);
      setEditSource(null);
      fetchData();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const handleDeleteSource = async (s) => {
    if (!confirm(`Remove "${s.name}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/income/sources/${s.source_id}?delete_events=true`, { method: 'DELETE', credentials: 'include' });
      await parseResponse(res);
      toast.success('Removed');
      fetchData();
    } catch (e) { toast.error(e.message || 'Could not remove income source. Please try again.'); }
  };

  const handleToggleActive = async (s) => {
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/income/sources/${s.source_id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ is_active: !s.is_active })
      });
      await parseResponse(res);
      toast.success(s.is_active ? 'Marked inactive' : 'Marked active');
      fetchData();
    } catch (e) { toast.error(e.message || 'Could not update status. Please try again.'); }
  };

  const handleSaveEvent = async (data) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/income/events`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ income_source_id: data.income_source_id, expected_amount: parseFloat(data.expected_amount), expected_date: data.expected_date })
      });
      // Parse response exactly once
      await parseResponse(res);
      toast.success('Added');
      setEventOpen(false);
      fetchData();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const handleMarkReceived = async (data) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/income/events/${data.event_id}/mark-received?actual_amount=${data.actual_amount}&actual_date=${data.actual_date}`, { method: 'POST', credentials: 'include' });
      // Parse response exactly once
      await parseResponse(res);
      toast.success('Marked received');
      setReceivedOpen(false);
      setMarkingEvent(null);
      fetchData();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const handleMarkMissed = async (e) => {
    if (!confirm(`Mark "${e.source_name}" as missed?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/income/events/${e.event_id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ status: 'missed' })
      });
      await parseResponse(res);
      toast.success('Marked missed');
      fetchData();
    } catch (e) { toast.error(e.message || 'Could not mark as missed. Please try again.'); }
  };

  const handleChangeStatus = async (event, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/income/events/${event.event_id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      await parseResponse(res);
      toast.success(`Status changed to ${newStatus}`);
      fetchData();
    } catch (e) { toast.error(e.message || 'Could not change status. Please try again.'); }
  };

  // Edit Event state
  const [editEventOpen, setEditEventOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  const handleEditEvent = (event) => {
    // Find the parent source to get its rhythm settings
    const parentSource = sources.find(s => s.source_id === event.income_source_id);
    setEditingEvent({
      ...event,
      expected_amount: event.expected_amount || '',
      expected_date: event.expected_date || '',
      actual_amount: event.actual_amount || '',
      actual_date: event.actual_date || '',
      notes: event.notes || '',
      status: event.status || 'expected',
      // Source-level rhythm fields
      recurring: parentSource?.recurring ?? event.source_recurring ?? false,
      frequency: parentSource?.frequency ?? event.source_frequency ?? 'monthly',
      auto_generate: parentSource?.auto_generate ?? true,
      anchor_day_of_week: parentSource?.anchor_day_of_week ?? '',
      anchor_day_of_month: parentSource?.anchor_day_of_month ?? '',
    });
    setEditEventOpen(true);
  };

  const handleSaveEditEvent = async () => {
    if (!editingEvent) return;
    setSaving(true);
    try {
      // 1. Update the event itself
      const eventUpdates = {};
      if (editingEvent.expected_amount) eventUpdates.expected_amount = parseFloat(editingEvent.expected_amount);
      if (editingEvent.expected_date) eventUpdates.expected_date = editingEvent.expected_date;
      if (editingEvent.actual_amount) eventUpdates.actual_amount = parseFloat(editingEvent.actual_amount);
      if (editingEvent.actual_date) eventUpdates.actual_date = editingEvent.actual_date;
      if (editingEvent.status) eventUpdates.status = editingEvent.status;
      if (editingEvent.notes !== undefined) eventUpdates.notes = editingEvent.notes;
      
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/income/events/${editingEvent.event_id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(eventUpdates)
      });
      await parseResponse(res);

      // 2. If rhythm changed, update the parent source
      const parentSource = sources.find(s => s.source_id === editingEvent.income_source_id);
      const rhythmChanged = (
        (editingEvent.recurring !== (parentSource?.recurring ?? false)) ||
        (editingEvent.recurring && editingEvent.frequency !== parentSource?.frequency) ||
        (editingEvent.recurring && editingEvent.auto_generate !== parentSource?.auto_generate)
      );

      if (rhythmChanged && editingEvent.income_source_id) {
        const sourceUpdates = {
          recurring: editingEvent.recurring,
          frequency: editingEvent.recurring ? editingEvent.frequency : parentSource?.frequency,
          auto_generate: editingEvent.recurring ? editingEvent.auto_generate : false,
          anchor_day_of_week: (editingEvent.recurring && (editingEvent.frequency === 'weekly' || editingEvent.frequency === 'fortnightly') && editingEvent.anchor_day_of_week !== '') ? parseInt(editingEvent.anchor_day_of_week) : null,
          anchor_day_of_month: (editingEvent.recurring && editingEvent.frequency === 'monthly' && editingEvent.anchor_day_of_month !== '') ? parseInt(editingEvent.anchor_day_of_month) : null,
        };
        await fetch(`${API_URL}/api/households/${household.household_id}/income/sources/${editingEvent.income_source_id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(sourceUpdates)
        });

        // 3. If just turned on recurring + auto_generate, generate future events
        if (editingEvent.recurring && editingEvent.auto_generate && !(parentSource?.recurring && parentSource?.auto_generate)) {
          await fetch(`${API_URL}/api/households/${household.household_id}/income/sources/${editingEvent.income_source_id}/generate-events`, {
            method: 'POST', credentials: 'include'
          }).catch(() => {}); // Best effort
        }

        toast.success('This income will now repeat each cycle');
      } else {
        toast.success('Event updated');
      }

      setEditEventOpen(false);
      setEditingEvent(null);
      fetchData();
    } catch (e) { toast.error(e.message || 'Could not update event. Please try again.'); } finally { setSaving(false); }
  };

  const [matchDiag, setMatchDiag] = useState(null);

  const handleRunMatching = async () => {
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/income/match`, {
        method: 'POST', credentials: 'include'
      });
      const data = await parseResponse(res);
      if (data.matches_found > 0) {
        toast.success(`Matched ${data.matches_found} income event${data.matches_found > 1 ? 's' : ''} from bank transactions`);
        fetchData();
      } else {
        // Show diagnostic dialog
        setMatchDiag(data);
      }
    } catch (e) { toast.error(e.message || 'Matching failed'); }
  };

  if (fetchError) {
    return (
      <div className="p-6 md:p-8 lg:p-10 max-w-4xl mx-auto">
        <FetchErrorState onRetry={fetchData} message="We couldn't load your income data. Your data is safe — this is just a connection issue." />
      </div>
    );
  }

  return (
    <>
    <div className="p-6 md:p-8 lg:p-10 max-w-4xl mx-auto space-y-6" data-testid="income-page">
        <OverviewPanel dashboard={dashboard} currency={currency} loading={loading} />
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRunMatching}
            className="rounded-full text-xs gap-1.5"
            data-testid="run-matching-btn"
          >
            <Zap className="w-3.5 h-3.5" />
            Match Bank Deposits
          </Button>
        </div>
        <UpcomingIncomePanel
          events={events}
          currency={currency}
          onMarkReceived={(e) => { setMarkingEvent(e); setReceivedOpen(true); }}
          onMarkMissed={handleMarkMissed}
          onEditEvent={handleEditEvent}
          onChangeStatus={handleChangeStatus}
          onAddEvent={() => setEventOpen(true)}
          loading={loading}
        />
        <IncomeSourcesPanel
          sources={sources}
          accounts={accounts}
          currency={currency}
          onAdd={() => { setEditSource(null); setSourceOpen(true); }}
          onEdit={(s) => { setEditSource(s); setSourceOpen(true); }}
          onDelete={handleDeleteSource}
          onToggleActive={handleToggleActive}
          loading={loading}
        />
        <InfoNote />
      </div>

      <SourceDialog open={sourceOpen} onOpenChange={setSourceOpen} source={editSource} onSave={handleSaveSource} members={members} accounts={accounts} loading={saving} />
      <EventDialog open={eventOpen} onOpenChange={setEventOpen} sources={sources} onSave={handleSaveEvent} loading={saving} />
      <ReceivedDialog open={receivedOpen} onOpenChange={setReceivedOpen} event={markingEvent} onConfirm={handleMarkReceived} currency={currency} loading={saving} />

      {/* Matching Diagnostics Dialog */}
      <Dialog open={!!matchDiag} onOpenChange={() => setMatchDiag(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" aria-describedby="diag-desc">
          <DialogHeader>
            <DialogTitle className="font-heading">Match Results</DialogTitle>
          </DialogHeader>
          <p id="diag-desc" className="sr-only">Income matching diagnostic details</p>
          {matchDiag && (() => {
            const diag = matchDiag.diagnostics || {};
            const deposits = diag.unmatched_deposits || [];
            const noRules = diag.sources_without_rules || [];
            return (
              <div className="space-y-4 text-sm" data-testid="match-diagnostics">
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
                  No new matches found right now. We checked {matchDiag.sources_checked} source{matchDiag.sources_checked !== 1 ? 's' : ''}.
                </div>

                {noRules.length > 0 && (
                  <div>
                    <p className="font-medium text-slate-700 dark:text-slate-300 mb-2">Sources without keywords:</p>
                    <div className="space-y-1">
                      {noRules.map(s => (
                        <div key={s.source_id} className="px-3 py-2 rounded bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400">
                          {s.name} ({formatCurrency(s.expected_amount, currency)})
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {deposits.length > 0 ? (
                  <div>
                    <p className="font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Unmatched deposits ({deposits.length}):
                    </p>
                    <div className="space-y-1.5">
                      {deposits.map((d, i) => (
                        <div key={i} className="px-3 py-2 rounded bg-slate-50 dark:bg-slate-800/50">
                          <div className="flex justify-between text-slate-700 dark:text-slate-300">
                            <span className="font-mono text-xs truncate mr-2">{d.description}</span>
                            <span className="font-medium tabular-nums whitespace-nowrap">{formatCurrency(d.amount, currency)}</span>
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {d.date} · normalized: <span className="font-mono">{d.description_normalized}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-3">
                      Use the normalized text above to choose keywords for your income sources.
                    </p>
                  </div>
                ) : (
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 text-slate-500">
                    No unmatched deposits found right now. Your bank account may need a moment to update.
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMatchDiag(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={editEventOpen} onOpenChange={(open) => { setEditEventOpen(open); if (!open) setEditingEvent(null); }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" aria-describedby="edit-event-desc">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Income Event</DialogTitle>
          </DialogHeader>
          <p id="edit-event-desc" className="sr-only">Edit income event details</p>
          {editingEvent && (
            <div className="space-y-4" data-testid="edit-event-form">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Source</label>
                <p className="text-sm font-medium mt-1">{editingEvent.source_name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Expected Amount</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingEvent.expected_amount}
                    onChange={e => setEditingEvent(prev => ({ ...prev, expected_amount: e.target.value }))}
                    data-testid="edit-event-expected-amount"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{editingEvent.recurring ? 'Starts on' : 'Expected Date'}</label>
                  <Input
                    type="date"
                    value={editingEvent.expected_date}
                    onChange={e => setEditingEvent(prev => ({ ...prev, expected_date: e.target.value }))}
                    data-testid="edit-event-expected-date"
                  />
                </div>
              </div>

              {/* ── Rhythm section ── */}
              <div className="border-t pt-4 mt-1">
                <div className="flex items-center gap-2 mb-3">
                  <RefreshCw className="w-3.5 h-3.5 text-slate-400" />
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Repeats</label>
                  <div className="ml-auto">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!editingEvent.recurring}
                      onClick={() => setEditingEvent(prev => ({ ...prev, recurring: !prev.recurring, auto_generate: !prev.recurring ? true : prev.auto_generate }))}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        editingEvent.recurring ? 'bg-[#1a4d3e]' : 'bg-slate-300 dark:bg-slate-600'
                      }`}
                      data-testid="edit-event-recurring-toggle"
                    >
                      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                        editingEvent.recurring ? 'translate-x-[18px]' : 'translate-x-[3px]'
                      }`} />
                    </button>
                  </div>
                </div>
                {!editingEvent.recurring && (
                  <p className="text-xs text-slate-400 -mt-1">Set this once and we'll carry it forward each cycle</p>
                )}

                {editingEvent.recurring && (
                  <div className="space-y-3 animate-in fade-in-0 slide-in-from-top-2 duration-200">
                    <div className="space-y-1.5">
                      <label className="text-xs text-slate-500">Frequency</label>
                      <div className="flex gap-1.5">
                        {[
                          { value: 'weekly', label: 'Weekly' },
                          { value: 'fortnightly', label: 'Fortnightly' },
                          { value: 'monthly', label: 'Monthly' },
                        ].map(f => (
                          <button
                            key={f.value}
                            type="button"
                            onClick={() => setEditingEvent(prev => ({ ...prev, frequency: f.value, anchor_day_of_week: '', anchor_day_of_month: '' }))}
                            className={`flex-1 py-2 px-2 rounded-lg text-xs transition-colors ${
                              editingEvent.frequency === f.value
                                ? 'bg-[#1a4d3e] text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400'
                            }`}
                            data-testid={`edit-freq-${f.value}-btn`}
                          >
                            {f.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Day selector */}
                    {(editingEvent.frequency === 'weekly' || editingEvent.frequency === 'fortnightly') && (
                      <div className="space-y-1.5">
                        <label className="text-xs text-slate-500">Paid on</label>
                        <Select
                          value={editingEvent.anchor_day_of_week !== '' && editingEvent.anchor_day_of_week != null ? String(editingEvent.anchor_day_of_week) : ''}
                          onValueChange={(v) => setEditingEvent(prev => ({ ...prev, anchor_day_of_week: v }))}
                        >
                          <SelectTrigger className="rounded-lg" data-testid="edit-anchor-day-of-week">
                            <SelectValue placeholder="Select a day" />
                          </SelectTrigger>
                          <SelectContent>
                            {DAYS_OF_WEEK.map(d => (
                              <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {editingEvent.frequency === 'monthly' && (
                      <div className="space-y-1.5">
                        <label className="text-xs text-slate-500">Paid on</label>
                        <Select
                          value={editingEvent.anchor_day_of_month !== '' && editingEvent.anchor_day_of_month != null ? String(editingEvent.anchor_day_of_month) : ''}
                          onValueChange={(v) => setEditingEvent(prev => ({ ...prev, anchor_day_of_month: v }))}
                        >
                          <SelectTrigger className="rounded-lg" data-testid="edit-anchor-day-of-month">
                            <SelectValue placeholder="Select date" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                              <SelectItem key={d} value={String(d)}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <label className="flex items-center justify-between py-1 cursor-pointer" data-testid="edit-auto-generate-toggle">
                      <span className="text-xs text-slate-500">Automatically add future income</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!!editingEvent.auto_generate}
                        onClick={() => setEditingEvent(prev => ({ ...prev, auto_generate: !prev.auto_generate }))}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          editingEvent.auto_generate ? 'bg-[#1a4d3e]' : 'bg-slate-300 dark:bg-slate-600'
                        }`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                          editingEvent.auto_generate ? 'translate-x-[18px]' : 'translate-x-[3px]'
                        }`} />
                      </button>
                    </label>
                    <p className="text-xs text-slate-400 -mt-2">We'll create upcoming entries so your plan stays accurate</p>
                  </div>
                )}
              </div>

              {/* Actual + Status — only for existing events */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Actual Amount</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingEvent.actual_amount}
                    onChange={e => setEditingEvent(prev => ({ ...prev, actual_amount: e.target.value }))}
                    placeholder="Once received"
                    data-testid="edit-event-actual-amount"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Actual Date</label>
                  <Input
                    type="date"
                    value={editingEvent.actual_date}
                    onChange={e => setEditingEvent(prev => ({ ...prev, actual_date: e.target.value }))}
                    data-testid="edit-event-actual-date"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Status</label>
                <Select value={editingEvent.status} onValueChange={v => setEditingEvent(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger data-testid="edit-event-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expected">Expected</SelectItem>
                    <SelectItem value="received">Received</SelectItem>
                    <SelectItem value="missed">Missed</SelectItem>
                    <SelectItem value="pending">Late</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Notes</label>
                <Input
                  value={editingEvent.notes}
                  onChange={e => setEditingEvent(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes"
                  data-testid="edit-event-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditEventOpen(false); setEditingEvent(null); }}>Cancel</Button>
            <Button onClick={handleSaveEditEvent} disabled={saving} className="rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900" data-testid="save-edit-event-btn">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function IncomePage() {
  return <AppLayout><IncomeContent /></AppLayout>;
}
